import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
const PERIOD_COLUMN_WIDTH = 160;
const METRIC_COLUMN_WIDTH = 110;
const GROUP_HEADER_HEIGHT = 56;
const METRIC_HEADER_HEIGHT = 48;
const GRID_DIVIDER = "#919EAB1F";
const metrics = ["Min Temp", "Avg Temp", "Max Temp"];
const formatTemp = (value) => {
    if (value === null || value === undefined)
        return "-";
    return value.toFixed(1);
};
export const ReportsTemperatureGrid = ({ selectedNodes, rows, tableHeight, }) => {
    const metricColumnsCount = selectedNodes.length * 3;
    const metricColumnsTemplate = `repeat(${metricColumnsCount}, minmax(${METRIC_COLUMN_WIDTH}px, 1fr))`;
    const groupColumnsTemplate = `repeat(${selectedNodes.length}, minmax(${METRIC_COLUMN_WIDTH * 3}px, 1fr))`;
    const temperatureGridWidth = metricColumnsCount * METRIC_COLUMN_WIDTH;
    const temperatureTrackWidth = `max(100%, ${temperatureGridWidth}px)`;
    const nodeHeaderTrackRef = useRef(null);
    const metricHeaderTrackRef = useRef(null);
    const horizontalRafRef = useRef(null);
    const pendingScrollLeftRef = useRef(0);
    const periodScrollRef = useRef(null);
    const temperatureScrollRef = useRef(null);
    const syncingVerticalScrollRef = useRef(false);
    const syncVerticalScroll = (source, scrollTop) => {
        if (syncingVerticalScrollRef.current)
            return;
        syncingVerticalScrollRef.current = true;
        const target = source === "period" ? temperatureScrollRef.current : periodScrollRef.current;
        if (target) {
            target.scrollTop = scrollTop;
        }
        requestAnimationFrame(() => {
            syncingVerticalScrollRef.current = false;
        });
    };
    const syncHorizontalScroll = (scrollLeft) => {
        pendingScrollLeftRef.current = scrollLeft;
        if (horizontalRafRef.current !== null)
            return;
        horizontalRafRef.current = requestAnimationFrame(() => {
            const x = pendingScrollLeftRef.current;
            if (nodeHeaderTrackRef.current) {
                nodeHeaderTrackRef.current.style.transform = `translate3d(-${x}px, 0, 0)`;
            }
            if (metricHeaderTrackRef.current) {
                metricHeaderTrackRef.current.style.transform = `translate3d(-${x}px, 0, 0)`;
            }
            horizontalRafRef.current = null;
        });
    };
    return (React.createElement(Box, { sx: {
            maxWidth: "100%",
            height: tableHeight,
            maxHeight: tableHeight,
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
        } },
        React.createElement(Box, { sx: { display: "flex", flexDirection: "column", minHeight: 0, height: "100%" } },
            React.createElement(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: `${PERIOD_COLUMN_WIDTH}px minmax(0, 1fr)`,
                } },
                React.createElement(Box, { sx: { backgroundColor: "#F4F6F8", height: `${GROUP_HEADER_HEIGHT}px` } }),
                React.createElement(Box, { sx: { backgroundColor: "#F4F6F8" } },
                    React.createElement(Box, { ref: nodeHeaderTrackRef, sx: {
                            minWidth: `${temperatureGridWidth}px`,
                            width: temperatureTrackWidth,
                            transform: "translate3d(0, 0, 0)",
                            willChange: "transform",
                            display: "grid",
                            gridTemplateColumns: groupColumnsTemplate,
                        } }, selectedNodes.map((node, index) => (React.createElement(Box, { key: String(node.value), sx: {
                            height: `${GROUP_HEADER_HEIGHT}px`,
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#DBEEFA",
                            borderRight: index === selectedNodes.length - 1
                                ? "none"
                                : `1px solid ${GRID_DIVIDER}`,
                            borderTopLeftRadius: index === 0 ? "50px" : 0,
                            borderBottomLeftRadius: index === 0 ? "50px" : 0,
                            borderTopRightRadius: index === selectedNodes.length - 1 ? "50px" : 0,
                            borderBottomRightRadius: index === selectedNodes.length - 1 ? "50px" : 0,
                            color: "text.secondary",
                            fontWeight: 700,
                            boxShadow: "inset 0 -8px 0 #F4F6F8",
                        } },
                        React.createElement(Typography, { variant: "subtitle2" }, node.label))))))),
            React.createElement(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: `${PERIOD_COLUMN_WIDTH}px minmax(0, 1fr)`,
                } },
                React.createElement(Box, { sx: {
                        height: `${METRIC_HEADER_HEIGHT}px`,
                        px: 2,
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        fontWeight: 700,
                        color: "text.secondary",
                        backgroundColor: "common.white",
                        borderTopLeftRadius: "50px",
                        borderRight: `1px solid ${GRID_DIVIDER}`,
                        backgroundClip: "padding-box",
                    } },
                    React.createElement(Typography, { variant: "subtitle2" }, "Period")),
                React.createElement(Box, { sx: { overflow: "hidden", backgroundColor: "#F4F6F8" } },
                    React.createElement(Box, { ref: metricHeaderTrackRef, sx: {
                            minWidth: `${temperatureGridWidth}px`,
                            width: temperatureTrackWidth,
                            transform: "translate3d(0, 0, 0)",
                            willChange: "transform",
                            display: "grid",
                            gridTemplateColumns: metricColumnsTemplate,
                        } }, selectedNodes.map((node, nodeIndex) => metrics.map((metric, metricIndex) => (React.createElement(Box, { key: `${String(node.value)}-${metric}`, sx: {
                            height: `${METRIC_HEADER_HEIGHT}px`,
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                            borderRight: metricIndex === metrics.length - 1 &&
                                nodeIndex !== selectedNodes.length - 1
                                ? `1px solid ${GRID_DIVIDER}`
                                : "none",
                            borderTopRightRadius: nodeIndex === selectedNodes.length - 1 &&
                                metricIndex === metrics.length - 1
                                ? "50px"
                                : 0,
                            backgroundColor: "#FFFFFF",
                        } },
                        React.createElement(Typography, { variant: "body2", fontWeight: 600 }, metric)))))))),
            React.createElement(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: `${PERIOD_COLUMN_WIDTH}px minmax(0, 1fr)`,
                    flex: 1,
                    borderTop: `none`,
                    minHeight: 0, border: `1px solid ${GRID_DIVIDER}`,
                } },
                React.createElement(Box, { ref: periodScrollRef, sx: {
                        overflowY: "auto",
                        overflowX: "hidden",
                        scrollbarGutter: "stable",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                    }, onScroll: (event) => {
                        syncVerticalScroll("period", event.currentTarget.scrollTop);
                    } }, rows.map((row, rowIndex) => (React.createElement(Box, { key: `period-${row.period}-${rowIndex}`, sx: {
                        minHeight: 56,
                        px: 2,
                        py: 1.5,
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#F4F6F8",
                        borderRight: `1px solid ${GRID_DIVIDER}`,
                    } }, (() => {
                    var _a;
                    // Split "May 16, 2026 00:00 - 12:00" → date + time range on separate lines
                    const match = (_a = row.period) === null || _a === void 0 ? void 0 : _a.match(/^(.*\d{4})\s+(.+)$/);
                    return match ? (React.createElement(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.25 } },
                        React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { whiteSpace: "nowrap" } }, match[1]),
                        React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { whiteSpace: "nowrap" } }, match[2]))) : (React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { whiteSpace: "nowrap" } }, row.period));
                })())))),
                React.createElement(Box, { ref: temperatureScrollRef, sx: {
                        overflowX: "auto",
                        overflowY: "auto",
                        scrollbarGutter: "stable both-edges",
                    }, onScroll: (event) => {
                        syncHorizontalScroll(event.currentTarget.scrollLeft);
                        syncVerticalScroll("temperature", event.currentTarget.scrollTop);
                    } },
                    React.createElement(Box, { sx: { minWidth: `${temperatureGridWidth}px`, width: temperatureTrackWidth } }, rows.map((row, rowIndex) => (React.createElement(Box, { key: `temp-row-${row.period}-${rowIndex}`, sx: { display: "grid", gridTemplateColumns: metricColumnsTemplate } }, selectedNodes.map((node, nodeIndex) => {
                        const nodeData = row[String(node.value)];
                        const values = [
                            nodeData === null || nodeData === void 0 ? void 0 : nodeData.minTemp,
                            nodeData === null || nodeData === void 0 ? void 0 : nodeData.avgTemp,
                            nodeData === null || nodeData === void 0 ? void 0 : nodeData.maxTemp,
                        ];
                        return values.map((value, metricIndex) => (React.createElement(Box, { key: `${String(node.value)}-${rowIndex}-${metricIndex}`, sx: {
                                minHeight: 56,
                                py: 1.5,
                                boxSizing: "border-box",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRight: metricIndex === values.length - 1 &&
                                    nodeIndex !== selectedNodes.length - 1
                                    ? `1px solid ${GRID_DIVIDER}`
                                    : "none",
                                backgroundColor: "#F4F6F8",
                            } },
                            React.createElement(Typography, { variant: "body2", color: "text.secondary" }, formatTemp(value)))));
                    }))))))))));
};
